import { Trans, useLingui } from '@lingui/react/macro';
import { ArrowLeft, TriangleAlert } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { CommitWarning, CommitWarningGroup } from '@/domain/rotagen/commit-warnings';

/**
 * The step shown when Rotaready would accept the rota but raised warnings a
 * manager is allowed to override — for example a person ending up below their
 * contracted hours. Lists each warning in plain language, grouped by person,
 * and offers **Back** (to the proposal, nothing saved) or **Save anyway**.
 * Shown in place of the proposal inside the same modal, so it works for any
 * proposal view.
 */
export function CommitWarningsView({
  groups,
  onBack,
  onSaveAnyway,
  isSaving = false,
}: CommitWarningsViewProps) {
  return (
    <div className="flex max-h-[85vh] flex-col">
      <header className="flex items-start gap-3 border-b px-6 py-4">
        <TriangleAlert className="mt-1 size-5 shrink-0 text-warning-500" />
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">
            <Trans>Review warnings</Trans>
          </h2>
          <p className="text-[length:var(--body-font-size)] leading-[var(--body-line-height)] text-[var(--muted-foreground)]">
            <Trans>
              The rota can still be saved, but Rotaready flagged the following. Go back to change
              it, or save anyway.
            </Trans>
          </p>
        </div>
      </header>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
        {groups.map((group) => (
          <section key={group.workerName ?? 'other'} className="space-y-1">
            <h3 className="font-medium">{group.workerName ?? <Trans>Other warnings</Trans>}</h3>
            <ul className="list-disc space-y-1 pl-5 text-sm">
              {group.warnings.map((warning) => (
                <li key={warningKey(warning)}>
                  <WarningText warning={warning} />
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      <footer className="flex items-center justify-between gap-2 border-t px-6 py-4">
        <Button variant="outline" onClick={onBack} disabled={isSaving}>
          <ArrowLeft className="size-4" />
          <Trans>Back</Trans>
        </Button>
        <Button onClick={onSaveAnyway} disabled={isSaving}>
          {isSaving ? <Trans>Saving…</Trans> : <Trans>Save anyway</Trans>}
        </Button>
      </footer>
    </div>
  );
}

/** Identifies a warning within its group; repeats are already collapsed, so this is unique. */
function warningKey(warning: CommitWarning): string {
  return [warning.rule, warning.message, warning.currentValue, warning.comparatorValue].join('|');
}

/**
 * One warning in plain language. Contracted hours and days read as "under" or
 * "over" with the figures; any other rule shows rotagen's own message.
 */
function WarningText({ warning }: { warning: CommitWarning }) {
  const { t } = useLingui();
  const { rule, currentValue, comparatorValue, message } = warning;

  if (currentValue === undefined || comparatorValue === undefined) {
    return <>{message}</>;
  }

  const isUnder = currentValue < comparatorValue;
  if (rule === 'contractualHours') {
    const amounts = t`${currentValue} of ${comparatorValue}h`;
    return isUnder ? (
      <>{t`Would be under contracted hours (${amounts})`}</>
    ) : (
      <>{t`Would be over contracted hours (${amounts})`}</>
    );
  }
  if (rule === 'contractualDays') {
    const amounts = t`${currentValue} of ${comparatorValue} days`;
    return isUnder ? (
      <>{t`Would be under contracted days (${amounts})`}</>
    ) : (
      <>{t`Would be over contracted days (${amounts})`}</>
    );
  }
  return <>{message}</>;
}

interface CommitWarningsViewProps {
  /** The warnings rotagen raised, grouped by the person they concern. */
  groups: CommitWarningGroup[];
  /** Return to the proposal without saving anything. */
  onBack: () => void;
  /** Save the rota despite the warnings. */
  onSaveAnyway: () => void;
  /** Disables the actions while the save is in flight. */
  isSaving?: boolean;
}
